import { toast } from "../toast/toast";
import { Lab, Membership, Role, User } from "../../../backend/src/types";
import { ENDPOINT } from "../endpoint/endpoint";
import { errorMessage } from "../function/errorMessage";

type Response<T> = {
  data: T;
  error?: any;
};

export async function getRoleList(): Promise<Role[]> {
  try {
    const res = await fetch(ENDPOINT.role.getRoleList, {
      method: "GET",
    });

    const { data, error }: Response<Role[]> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("役割リストの取得に失敗しました", error);
    return [];
  }
}

interface GetUserMembershipPayload {
  targetUser: User;
}

export async function getUserMembership(
  payload: GetUserMembershipPayload,
): Promise<Membership[]> {
  try {
    const res = await fetch(ENDPOINT.role.getUserMembership, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: Response<Membership[]> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("権限リストの取得に失敗しました", error);
    return [];
  }
}

interface GetUserListPayload {
  user: User;
}

export async function getUserList(
  payload: GetUserListPayload,
): Promise<User[]> {
  try {
    const res = await fetch(ENDPOINT.user.getUserList, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: Response<User[]> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    const users = [...data].sort((a, b) => a.name.localeCompare(b.name, "ja"));
    return users;
  } catch (error) {
    errorMessage("ユーザーリストの取得に失敗しました", error);
    return [];
  }
}


interface AddUserPayload {
  user: User;
  memberships: Membership[];
  password: string;
}

// 追加結果。失敗時は原因(reason)を返し、CSV一括登録の要約表示に使う。
export type AddUserResult =
  | { ok: true; user: User }
  | { ok: false; status?: number; reason: string };

export async function addUser(
  payload: AddUserPayload,
): Promise<AddUserResult> {
  try {
    const res = await fetch(ENDPOINT.user.addUser, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: Response<User> = await res.json();

    if (!res.ok) {
      if (res.status === 409) {
        toast.error("すでに同じ学籍番号のユーザーが登録されています。");
        return { ok: false, status: 409, reason: "学籍番号が重複しています" };
      }
      errorMessage("ユーザーの追加に失敗しました。", error ?? "Server Error");
      return { ok: false, status: res.status, reason: "サーバーエラー" };
    }

    return { ok: true, user: data };
  } catch (error) {
    errorMessage("ユーザーの追加に失敗しました。", error);
    return { ok: false, reason: "通信エラー" };
  }
}

interface EditUserPayload {
  user: User;
  memberships: Membership[];
  isSameNo: boolean;
}

export async function editUser(payload: EditUserPayload): Promise<User | null> {
  try {
    const res = await fetch(ENDPOINT.user.editUser, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: Response<User> = await res.json();

    if (!res.ok) {
      if (res.status === 409) {
        toast.error("すでに同じ学籍番号のユーザーが登録されています。");
        return null;
      }
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("ユーザーの更新に失敗しました。", error);
    return null;
  }
}

interface DeleteUserPayload {
  target_user_id: number;
  tenant_id: number;
}

export async function deleteUser(payload: DeleteUserPayload) {
  try {
    const res = await fetch(ENDPOINT.user.deleteUser, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.status === 204) {
      return;
    }

    if (!res.ok) {
      const { error }: Response<null> = await res.json();
      throw new Error(error ?? "Server Error");
    }
  } catch (error) {
    errorMessage("ユーザーの削除に失敗しました。", error);
  }
}

// 本人のパスワード変更（現在のパスワード確認あり）。成功=true。失敗時は例外。
export async function changeMyPassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  const res = await fetch(ENDPOINT.user.changeMyPassword, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!res.ok) {
    const { error }: Response<null> = await res.json().catch(() => ({
      data: null,
    }));
    throw new Error(error ?? "パスワードの変更に失敗しました。");
  }
}

// 大学管理者による任意ユーザーのパスワード変更。失敗時は例外。
export async function resetUserPassword(
  target_user_id: number,
  newPassword: string
): Promise<void> {
  const res = await fetch(ENDPOINT.user.resetUserPassword, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target_user_id, newPassword }),
  });
  if (!res.ok) {
    const { error }: Response<null> = await res.json().catch(() => ({
      data: null,
    }));
    throw new Error(error ?? "パスワードの変更に失敗しました。");
  }
}

interface DeleteSessionPayload {
  user: User;
}

export async function deleteSession(payload: DeleteSessionPayload) {
  try {
    const res = await fetch(ENDPOINT.auth.deleteSession, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { error }: Response<null> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }
  } catch (error) {
    errorMessage("ログアウト処理に失敗しました。", error);
  }
}

interface ContextDeta {
  user: User;
  memberships: Membership[];
  labs: Lab[];
}

interface CheckSessionPayload {
  sessionId: string;
}

export async function loadContextDeta(
  payload: CheckSessionPayload,
): Promise<ContextDeta | null> {
  try {
    const res = await fetch(ENDPOINT.auth.loadContextDeta, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: Response<ContextDeta> = await res.json();

    if (!res.ok) {
      if (res.status === 401) {
        return null;
      } else if (res.status === 404) {
        return null;
      }
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("セッション情報の取得に失敗しました。", error);
    return null;
  }
}

interface LoginRes {
  user: User;
  sessionId: string;
  memberships: Membership[];
  labs: Lab[];
}

interface LoginPayload {
  student_no: string;
  password: string;
}

export async function login(payload: LoginPayload): Promise<LoginRes | null> {
  try {
    const res = await fetch(ENDPOINT.auth.login, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: Response<LoginRes | string> = await res.json();

    if (typeof data === "string") {
      toast.error(data);
      return null;
    }

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("ログインに失敗しました", error);
    return null;
  }
}
